/**
 * Custom exercise workout pages — 4 templates dispatched by exercise.template.
 *
 * Route: /exercise/custom/:exerciseKey
 *
 * The route loads the custom exercise metadata from IndexedDB and renders the
 * appropriate sub-component. All sub-components share styling with built-in
 * exercise pages (StairClimbing / Plank / DumbbellCurl / PushUp).
 */
import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSetAtom } from 'jotai'
import { Icon } from '@iconify/react'
import { toast } from '@/components/function/base/Toaster'
import { saveWorkout } from '../../stores/WorkoutStore'
import { updateCustomExerciseAtom } from '../../stores/SettingsStore'
import {
  db, getActiveSession, setActiveSession, clearActiveSession,
  type LocalCustomExercise, type ActiveSession,
} from '@/lib/db'
import { playBell, DEFAULT_BELL_COUNT } from '@/lib/bell'
import { PRESET_BY_KEY } from '@/lib/exerciseRegistry'
import { NumberField } from '@/components/function/NumberField'

const ACTIVE_SESSION_MAX_AGE_MS = 12 * 60 * 60 * 1000  // 12 hours

/**
 * Live elapsed for a wall-clock-based timer, given the persisted session
 * shape. Works whether the session is running or paused (uses `now` for
 * running, the captured pause instant for paused state).
 */
function computeElapsed(s: ActiveSession, nowMs: number): number {
  const startedMs = new Date(s.startedAtIso).getTime()
  const sinceStart = nowMs - startedMs
  const currentPause = s.pausedAtIso ? nowMs - new Date(s.pausedAtIso).getTime() : 0
  return Math.max(0, sinceStart - s.pausedTotalMs - currentPause)
}

// ============================================================================
// Shared helpers + UI atoms
// ============================================================================

function formatMmSs(ms: number) {
  const total = Math.floor(ms / 1000)
  const mm = Math.floor(total / 60)
  const ss = total % 60
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
}

interface HeaderProps {
  meta: LocalCustomExercise
  onBack: () => void
  onSettings?: () => void
  onTips?: () => void
}

function PageHeader({ meta, onBack, onSettings, onTips }: HeaderProps) {
  const isAerobic = meta.exerciseType === 'aerobic'
  return (
    <div className="flex-none pt-12 pb-4 px-6 flex items-center gap-3">
      <button
        onClick={onBack}
        className="w-10 h-10 rounded-full flex items-center justify-center flex-none"
        style={{ backgroundColor: '#1C1F2E' }}
      >
        <Icon icon="lucide:arrow-left" className="w-5 h-5 text-gray-400" />
      </button>
      <Icon icon={meta.icon} className={`w-5 h-5 ${isAerobic ? 'text-emerald-400' : 'text-violet-400'} flex-none`} />
      <h1 className="text-xl font-bold text-white truncate flex-1">{meta.name}</h1>
      <div className="flex items-center gap-2 flex-none">
        {onTips && (
          <button
            onClick={onTips}
            aria-label="动作要点"
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#1C1F2E' }}
          >
            <Icon icon="lucide:lightbulb" className="w-5 h-5 text-amber-300" />
          </button>
        )}
        {onSettings && (
          <button
            onClick={onSettings}
            aria-label="设置"
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#1C1F2E' }}
          >
            <Icon icon="lucide:settings-2" className="w-5 h-5 text-gray-400" />
          </button>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Tips bottom sheet — shows the preset exercise's form cues
// ============================================================================

function TipsSheet({ name, tips, onClose }: { name: string; tips: string[]; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60">
      <div className="w-full max-w-md rounded-t-3xl p-5 flex flex-col gap-4 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#1C1F2E' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon icon="lucide:lightbulb" className="w-5 h-5 text-amber-300" />
            <h3 className="text-white font-bold text-lg">{name} · 动作要点</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#12141D' }}>
            <Icon icon="lucide:x" className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <ol className="flex flex-col gap-2">
          {tips.map((tip, i) => (
            <li
              key={i}
              className="flex items-start gap-3 px-4 py-3 rounded-xl"
              style={{ backgroundColor: '#12141D' }}
            >
              <span className="w-6 h-6 rounded-full flex items-center justify-center bg-amber-300/10 text-amber-300 font-bold text-xs flex-none">
                {i + 1}
              </span>
              <span className="text-sm text-gray-200 leading-relaxed flex-1">{tip}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}

// ============================================================================
// Shared settings sheet — adjust restDuration for anaerobic templates
// ============================================================================

interface SettingsSheetProps {
  meta: LocalCustomExercise
  onClose: () => void
  onSaved: () => Promise<void> | void
}

function ExerciseSettingsSheet({ meta, onClose, onSaved }: SettingsSheetProps) {
  const update = useSetAtom(updateCustomExerciseAtom)
  const [restDuration, setRestDuration] = useState<number>(meta.restDuration ?? 60)
  const [defaultDuration, setDefaultDuration] = useState<number>(meta.defaultDuration ?? 60)
  const [weight, setWeight] = useState<number>(meta.weight ?? 0)
  const [quickReps1, setQuickReps1] = useState<number>(meta.quickReps1 ?? 10)
  const [quickReps2, setQuickReps2] = useState<number>(meta.quickReps2 ?? 15)
  const [quickReps3, setQuickReps3] = useState<number>(meta.quickReps3 ?? 20)
  const [submitting, setSubmitting] = useState(false)

  const isCountdown = meta.template === 'countdown'
  const isManualReps = meta.template === 'manual_reps'
  const supportsWeight = isManualReps

  const handleSubmit = () => {
    if (submitting) return
    setSubmitting(true)
    void (async () => {
      try {
        await update({
          exerciseKey: meta.exerciseKey,
          patch: {
            restDuration: Math.max(0, restDuration),
            ...(isCountdown ? { defaultDuration: Math.max(5, defaultDuration) } : {}),
            ...(supportsWeight ? { weight: Math.max(0, weight) } : {}),
            ...(isManualReps ? {
              quickReps1: Math.max(1, quickReps1),
              quickReps2: Math.max(1, quickReps2),
              quickReps3: Math.max(1, quickReps3),
            } : {}),
          },
        })
        await onSaved()
        toast.success('已保存')
      } catch (e) {
        toast.error((e as Error).message || '保存失败')
      } finally {
        setSubmitting(false)
      }
    })()
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60">
      <div className="w-full max-w-md rounded-t-3xl p-5 flex flex-col gap-5 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#1C1F2E' }}>
        <div className="flex items-center justify-between">
          <h3 className="text-white font-bold text-lg">运动参数</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#12141D' }}>
            <Icon icon="lucide:x" className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs text-gray-500 px-1">组间休息时长（秒）</label>
          <NumberField
            value={restDuration}
            onChange={setRestDuration}
            min={0}
            fallbackOnBlur={meta.restDuration ?? 60}
            className="w-full px-4 py-3 rounded-xl text-base text-white outline-none"
            style={{ backgroundColor: '#12141D', border: '1.5px solid #2A2D3A' }}
          />
        </div>

        {isCountdown && (
          <div className="flex flex-col gap-2">
            <label className="text-xs text-gray-500 px-1">单组默认时长（秒）</label>
            <NumberField
              value={defaultDuration}
              onChange={setDefaultDuration}
              min={5}
              fallbackOnBlur={meta.defaultDuration ?? 60}
              className="w-full px-4 py-3 rounded-xl text-base text-white outline-none"
              style={{ backgroundColor: '#12141D', border: '1.5px solid #2A2D3A' }}
            />
          </div>
        )}

        {supportsWeight && (
          <div className="flex flex-col gap-2">
            <label className="text-xs text-gray-500 px-1">默认重量（kg，自重填 0）</label>
            <NumberField
              value={weight}
              onChange={setWeight}
              min={0}
              fallbackOnBlur={meta.weight ?? 0}
              className="w-full px-4 py-3 rounded-xl text-base text-white outline-none"
              style={{ backgroundColor: '#12141D', border: '1.5px solid #2A2D3A' }}
            />
          </div>
        )}

        {isManualReps && (
          <div className="flex flex-col gap-2">
            <label className="text-xs text-gray-500 px-1">快捷次数（3 个一键填入按钮）</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: quickReps1, set: setQuickReps1, fb: meta.quickReps1 ?? 10 },
                { value: quickReps2, set: setQuickReps2, fb: meta.quickReps2 ?? 15 },
                { value: quickReps3, set: setQuickReps3, fb: meta.quickReps3 ?? 20 },
              ].map((q, i) => (
                <NumberField
                  key={i}
                  value={q.value}
                  onChange={q.set}
                  min={1}
                  fallbackOnBlur={q.fb}
                  className="w-full px-3 py-3 rounded-xl text-base text-white text-center outline-none"
                  style={{ backgroundColor: '#12141D', border: '1.5px solid #2A2D3A' }}
                />
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 py-3 rounded-xl text-gray-400 font-bold text-sm"
            style={{ backgroundColor: '#12141D' }}
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-3 rounded-xl text-white font-bold text-sm disabled:opacity-60"
            style={{ backgroundColor: '#10B981', borderBottom: '4px solid #059669' }}
          >
            {submitting ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Template 1: timer (start → end) — aerobic, no rest setting
// ============================================================================

function CustomTimer({ meta, onBack, onSettings, onTips }: { meta: LocalCustomExercise; onBack: () => void; onSettings?: () => void; onTips?: () => void }) {
  type State = 'idle' | 'running' | 'paused' | 'finished'
  const [state, setState] = useState<State>('idle')
  const [elapsedMs, setElapsedMs] = useState(0)
  const [summaryMs, setSummaryMs] = useState(0)
  const [resumePrompt, setResumePrompt] = useState<ActiveSession | null>(null)

  const sessionRef = useRef<ActiveSession | null>(null)
  const rafRef = useRef<number>(0)

  const tick = useCallback(() => {
    if (!sessionRef.current) return
    setElapsedMs(computeElapsed(sessionRef.current, Date.now()))
    rafRef.current = requestAnimationFrame(tick)
  }, [])

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }, [])

  // On mount: check for an in-progress session matching this exercise
  useEffect(() => {
    void (async () => {
      const existing = await getActiveSession()
      if (!existing || existing.exerciseKey !== meta.exerciseKey || existing.template !== 'timer') return
      const ageMs = Date.now() - new Date(existing.startedAtIso).getTime()
      if (ageMs > ACTIVE_SESSION_MAX_AGE_MS) {
        await clearActiveSession()
        return
      }
      setResumePrompt(existing)
    })()
  }, [meta.exerciseKey])

  const handleResumeContinue = () => {
    if (!resumePrompt) return
    sessionRef.current = resumePrompt
    setResumePrompt(null)
    setElapsedMs(computeElapsed(resumePrompt, Date.now()))
    if (resumePrompt.pausedAtIso) {
      setState('paused')
    } else {
      setState('running')
      rafRef.current = requestAnimationFrame(tick)
    }
  }
  const handleResumeDiscard = async () => {
    await clearActiveSession()
    setResumePrompt(null)
  }

  const handleStart = async () => {
    const s: ActiveSession = {
      exerciseKey: meta.exerciseKey,
      template: 'timer',
      startedAtIso: new Date().toISOString(),
      pausedTotalMs: 0,
      pausedAtIso: null,
    }
    sessionRef.current = s
    await setActiveSession(s)
    setElapsedMs(0)
    setState('running')
    rafRef.current = requestAnimationFrame(tick)
  }
  const handlePause = async () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    if (sessionRef.current) {
      sessionRef.current = { ...sessionRef.current, pausedAtIso: new Date().toISOString() }
      await setActiveSession(sessionRef.current)
    }
    setState('paused')
  }
  const handleResume = async () => {
    if (sessionRef.current?.pausedAtIso) {
      const addMs = Date.now() - new Date(sessionRef.current.pausedAtIso).getTime()
      sessionRef.current = {
        ...sessionRef.current,
        pausedTotalMs: sessionRef.current.pausedTotalMs + addMs,
        pausedAtIso: null,
      }
      await setActiveSession(sessionRef.current)
    }
    setState('running')
    rafRef.current = requestAnimationFrame(tick)
  }
  const handleFinish = async () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    const s = sessionRef.current
    if (!s) { setState('finished'); return }
    const totalMs = computeElapsed(s, Date.now())
    setSummaryMs(totalMs)
    setState('finished')
    await clearActiveSession()
    sessionRef.current = null

    if (meta.exerciseType === 'aerobic' && totalMs < 60_000) {
      toast.success('时长不足 1 分钟，未保存')
      return
    }
    try {
      await saveWorkout({
        exerciseKey: meta.exerciseKey,
        exerciseType: meta.exerciseType,
        startedAt: new Date(s.startedAtIso),
        endedAt: new Date(),
        durationSeconds: Math.round(totalMs / 1000),
      })
      toast.success('运动记录已保存')
    } catch {
      toast.error('保存失败')
    }
  }

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: '#12141D' }}>
      <PageHeader meta={meta} onBack={onBack} onSettings={onSettings} onTips={onTips} />
      <main className="flex-1 flex flex-col items-center px-6 pt-12">
        <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">
          {state === 'idle' && '准备开始'}
          {state === 'running' && '运动中'}
          {state === 'paused' && '已暂停'}
          {state === 'finished' && '运动结束'}
        </span>
        <div className="mt-4 text-7xl font-mono font-extrabold tracking-tighter text-white tabular-nums">
          {formatMmSs(state === 'finished' ? summaryMs : elapsedMs)}
        </div>
      </main>
      <BottomActions
        state={state}
        onStart={handleStart}
        onPrimary={handleFinish}
        onPause={handlePause}
        onResume={handleResume}
        onBack={onBack}
        primaryLabel="结束"
      />
      {resumePrompt && (
        <ResumeSessionPrompt
          name={meta.name}
          session={resumePrompt}
          onContinue={handleResumeContinue}
          onDiscard={handleResumeDiscard}
        />
      )}
    </div>
  )
}

// ============================================================================
// Resume prompt for aerobic timers — shared by CustomTimer / CustomLapsTimer
// ============================================================================

function ResumeSessionPrompt({ name, session, onContinue, onDiscard }: {
  name: string
  session: ActiveSession
  onContinue: () => void
  onDiscard: () => void | Promise<void>
}) {
  const elapsed = computeElapsed(session, Date.now())
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60">
      <div className="w-full max-w-md rounded-t-3xl p-5 flex flex-col gap-4" style={{ backgroundColor: '#1C1F2E' }}>
        <h3 className="text-white font-bold text-lg">上次的训练未结束</h3>
        <p className="text-sm text-gray-400 leading-relaxed">
          <span className="text-white font-medium">「{name}」</span>{' '}
          已经进行了{' '}
          <span className="text-emerald-400 font-mono font-bold">{formatMmSs(elapsed)}</span>
          {session.pausedAtIso && <span className="text-amber-400">（已暂停）</span>}
          。
        </p>
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => void onDiscard()}
            className="flex-1 py-3 rounded-xl text-gray-400 font-bold text-sm"
            style={{ backgroundColor: '#12141D' }}
          >
            丢弃
          </button>
          <button
            onClick={onContinue}
            className="flex-1 py-3 rounded-xl text-white font-bold text-sm"
            style={{ backgroundColor: '#10B981', borderBottom: '4px solid #059669' }}
          >
            继续训练
          </button>
        </div>
      </div>
    </div>
  )
}

interface BottomActionsProps {
  state: 'idle' | 'running' | 'paused' | 'finished'
  onStart: () => void
  onPrimary: () => void
  onPause: () => void
  onResume: () => void
  onBack: () => void
  primaryLabel: string
}

function BottomActions({ state, onStart, onPrimary, onPause, onResume, onBack, primaryLabel }: BottomActionsProps) {
  return (
    <div className="flex-none px-6 pb-10 flex gap-4">
      {state === 'idle' && (
        <button
          onClick={onStart}
          className="w-full py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all"
          style={{ backgroundColor: '#10B981', borderColor: '#059669' }}
        >开始运动</button>
      )}
      {state === 'running' && (
        <button
          onClick={onPause}
          className="w-full py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all"
          style={{ backgroundColor: '#F59E0B', borderColor: '#D97706' }}
        >暂停</button>
      )}
      {state === 'paused' && (
        <>
          <button
            onClick={onPrimary}
            className="flex-1 py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all"
            style={{ backgroundColor: '#EF4444', borderColor: '#DC2626' }}
          >{primaryLabel}</button>
          <button
            onClick={onResume}
            className="flex-1 py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all"
            style={{ backgroundColor: '#10B981', borderColor: '#059669' }}
          >继续</button>
        </>
      )}
      {state === 'finished' && (
        <button
          onClick={onBack}
          className="w-full py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all"
          style={{ backgroundColor: '#1C1F2E', borderColor: '#151722' }}
        >返回首页</button>
      )}
    </div>
  )
}

// ============================================================================
// Template 1b: laps_timer — timer with optional lap markers (爬楼梯/跑步等)
// ============================================================================

function CustomLapsTimer({ meta, onBack, onSettings, onTips }: { meta: LocalCustomExercise; onBack: () => void; onSettings?: () => void; onTips?: () => void }) {
  type State = 'idle' | 'running' | 'paused' | 'finished'
  interface Lap { lapNumber: number; elapsedMs: number }

  const [state, setState] = useState<State>('idle')
  const [elapsedMs, setElapsedMs] = useState(0)
  const [laps, setLaps] = useState<Lap[]>([])
  const [summaryMs, setSummaryMs] = useState(0)
  const [resumePrompt, setResumePrompt] = useState<ActiveSession | null>(null)

  const sessionRef = useRef<ActiveSession | null>(null)
  // Sum of lap durations already taken — used to derive the current in-progress lap
  const consumedLapsMsRef = useRef<number>(0)
  const rafRef = useRef<number>(0)

  const tick = useCallback(() => {
    if (!sessionRef.current) return
    setElapsedMs(computeElapsed(sessionRef.current, Date.now()))
    rafRef.current = requestAnimationFrame(tick)
  }, [])

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }, [])

  useEffect(() => {
    void (async () => {
      const existing = await getActiveSession()
      if (!existing || existing.exerciseKey !== meta.exerciseKey || existing.template !== 'laps_timer') return
      const ageMs = Date.now() - new Date(existing.startedAtIso).getTime()
      if (ageMs > ACTIVE_SESSION_MAX_AGE_MS) {
        await clearActiveSession()
        return
      }
      setResumePrompt(existing)
    })()
  }, [meta.exerciseKey])

  const handleResumeContinue = () => {
    if (!resumePrompt) return
    sessionRef.current = resumePrompt
    const restoredLaps = (resumePrompt.laps ?? []).map((ms, i) => ({ lapNumber: i + 1, elapsedMs: ms }))
    setLaps(restoredLaps)
    consumedLapsMsRef.current = restoredLaps.reduce((acc, l) => acc + l.elapsedMs, 0)
    setResumePrompt(null)
    setElapsedMs(computeElapsed(resumePrompt, Date.now()))
    if (resumePrompt.pausedAtIso) {
      setState('paused')
    } else {
      setState('running')
      rafRef.current = requestAnimationFrame(tick)
    }
  }
  const handleResumeDiscard = async () => {
    await clearActiveSession()
    setResumePrompt(null)
  }

  const handleStart = async () => {
    const s: ActiveSession = {
      exerciseKey: meta.exerciseKey,
      template: 'laps_timer',
      startedAtIso: new Date().toISOString(),
      pausedTotalMs: 0,
      pausedAtIso: null,
      laps: [],
    }
    sessionRef.current = s
    consumedLapsMsRef.current = 0
    await setActiveSession(s)
    setElapsedMs(0)
    setLaps([])
    setState('running')
    rafRef.current = requestAnimationFrame(tick)
  }
  const handlePause = async () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    if (sessionRef.current) {
      sessionRef.current = { ...sessionRef.current, pausedAtIso: new Date().toISOString() }
      await setActiveSession(sessionRef.current)
    }
    setState('paused')
  }
  const handleResume = async () => {
    if (sessionRef.current?.pausedAtIso) {
      const addMs = Date.now() - new Date(sessionRef.current.pausedAtIso).getTime()
      sessionRef.current = {
        ...sessionRef.current,
        pausedTotalMs: sessionRef.current.pausedTotalMs + addMs,
        pausedAtIso: null,
      }
      await setActiveSession(sessionRef.current)
    }
    setState('running')
    rafRef.current = requestAnimationFrame(tick)
  }
  const handleLap = async () => {
    if (!sessionRef.current) return
    const total = computeElapsed(sessionRef.current, Date.now())
    const lapMs = total - consumedLapsMsRef.current
    consumedLapsMsRef.current = total
    const nextLaps = [...laps, { lapNumber: laps.length + 1, elapsedMs: lapMs }]
    setLaps(nextLaps)
    sessionRef.current = { ...sessionRef.current, laps: nextLaps.map(l => l.elapsedMs) }
    await setActiveSession(sessionRef.current)
  }
  const handleFinish = async () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    const s = sessionRef.current
    if (!s) { setState('finished'); return }
    const totalMs = computeElapsed(s, Date.now())
    setSummaryMs(totalMs)
    setState('finished')
    await clearActiveSession()
    sessionRef.current = null

    if (meta.exerciseType === 'aerobic' && totalMs < 60_000) {
      toast.success('时长不足 1 分钟，未保存')
      return
    }
    try {
      await saveWorkout({
        exerciseKey: meta.exerciseKey,
        exerciseType: meta.exerciseType,
        startedAt: new Date(s.startedAtIso),
        endedAt: new Date(),
        durationSeconds: Math.round(totalMs / 1000),
        summary: { laps: laps.length, lapTimes: laps.map(l => Math.round(l.elapsedMs / 1000)) },
      })
      toast.success('运动记录已保存')
    } catch {
      toast.error('保存失败')
    }
  }

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: '#12141D' }}>
      <PageHeader meta={meta} onBack={onBack} onSettings={onSettings} onTips={onTips} />
      <main className="flex-1 flex flex-col items-center px-6 pt-8 overflow-y-auto pb-6">
        <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">
          {state === 'idle' && '准备开始'}
          {state === 'running' && '运动中'}
          {state === 'paused' && '已暂停'}
          {state === 'finished' && '运动结束'}
        </span>
        <div className="mt-4 text-7xl font-mono font-extrabold tracking-tighter text-white tabular-nums">
          {formatMmSs(state === 'finished' ? summaryMs : elapsedMs)}
        </div>

        {laps.length > 0 && (
          <div className="w-full mt-10 flex flex-col gap-2">
            <h3 className="text-sm font-bold text-gray-500 px-1">分段记录</h3>
            {laps.map((lap) => (
              <div
                key={lap.lapNumber}
                className="flex justify-between items-center px-4 py-3 rounded-2xl"
                style={{ backgroundColor: '#1C1F2E' }}
              >
                <span className="text-white font-bold text-sm">第 {lap.lapNumber} 段</span>
                <span className="text-emerald-400 font-mono font-bold text-sm">{formatMmSs(lap.elapsedMs)}</span>
              </div>
            ))}
          </div>
        )}
      </main>

      <div className="flex-none px-6 pb-10 flex gap-4">
        {state === 'idle' && (
          <button
            onClick={handleStart}
            className="w-full py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all"
            style={{ backgroundColor: '#10B981', borderColor: '#059669' }}
          >开始运动</button>
        )}
        {state === 'running' && (
          <>
            <button
              onClick={handleLap}
              className="flex-1 py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all"
              style={{ backgroundColor: '#3B82F6', borderColor: '#2563EB' }}
            >记录一圈</button>
            <button
              onClick={handlePause}
              className="flex-1 py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all"
              style={{ backgroundColor: '#F59E0B', borderColor: '#D97706' }}
            >暂停</button>
          </>
        )}
        {state === 'paused' && (
          <>
            <button
              onClick={handleFinish}
              className="flex-1 py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all"
              style={{ backgroundColor: '#EF4444', borderColor: '#DC2626' }}
            >结束</button>
            <button
              onClick={handleResume}
              className="flex-1 py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all"
              style={{ backgroundColor: '#10B981', borderColor: '#059669' }}
            >继续</button>
          </>
        )}
        {state === 'finished' && (
          <button
            onClick={onBack}
            className="w-full py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all"
            style={{ backgroundColor: '#1C1F2E', borderColor: '#151722' }}
          >返回首页</button>
        )}
      </div>
      {resumePrompt && (
        <ResumeSessionPrompt
          name={meta.name}
          session={resumePrompt}
          onContinue={handleResumeContinue}
          onDiscard={handleResumeDiscard}
        />
      )}
    </div>
  )
}

// ============================================================================
// Template 2: countdown — fixed-duration timer, then rest, then save
// ============================================================================

function CustomCountdown({ meta, onBack, onSettings, onTips }: { meta: LocalCustomExercise; onBack: () => void; onSettings?: () => void; onTips?: () => void }) {
  type Phase = 'idle' | 'set' | 'set_ringing' | 'rest' | 'rest_ringing' | 'finished'
  const [phase, setPhase] = useState<Phase>('idle')
  const [setDuration, setSetDuration] = useState<number>(meta.defaultDuration ?? 60)  // initial from meta, adjustable per session
  const [remainingMs, setRemainingMs] = useState(0)
  const [completedSets, setCompletedSets] = useState(0)
  const [totalSeconds, setTotalSeconds] = useState(0)
  const startedAtRef = useRef<Date>(new Date())
  const phaseEndRef = useRef<number>(0)
  const rafRef = useRef<number>(0)
  const cancelBellRef = useRef<(() => void) | null>(null)

  const restDur = meta.restDuration ?? 30

  const tick = useCallback(() => {
    const remaining = Math.max(0, phaseEndRef.current - performance.now())
    setRemainingMs(remaining)
    if (remaining <= 0) {
      // phase ends; caller's effect will handle transition
      return
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [])

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    if (cancelBellRef.current) { cancelBellRef.current(); cancelBellRef.current = null }
  }, [])

  const startRest = useCallback(() => {
    phaseEndRef.current = performance.now() + restDur * 1000
    setRemainingMs(restDur * 1000)
    setPhase('rest')
    rafRef.current = requestAnimationFrame(tick)
  }, [restDur, tick])

  // When remainingMs hits 0 inside a phase, ring the bell then transition
  useEffect(() => {
    if (remainingMs > 0 || phase === 'idle' || phase === 'finished' || phase === 'set_ringing' || phase === 'rest_ringing') return
    if (phase === 'set') {
      setCompletedSets((s) => s + 1)
      setTotalSeconds((t) => t + setDuration)
      setPhase('set_ringing')
      cancelBellRef.current = playBell(DEFAULT_BELL_COUNT, () => {
        cancelBellRef.current = null
        startRest()
      })
    } else if (phase === 'rest') {
      setPhase('rest_ringing')
      cancelBellRef.current = playBell(DEFAULT_BELL_COUNT, () => {
        cancelBellRef.current = null
        setPhase('idle')
      })
    }
  }, [remainingMs, phase, setDuration, startRest])

  const startSet = () => {
    if (phase === 'idle' && completedSets === 0) {
      startedAtRef.current = new Date()
    }
    phaseEndRef.current = performance.now() + setDuration * 1000
    setRemainingMs(setDuration * 1000)
    setPhase('set')
    rafRef.current = requestAnimationFrame(tick)
  }

  const stopBell = () => {
    if (cancelBellRef.current) {
      cancelBellRef.current()
      cancelBellRef.current = null
    }
  }

  const skipRest = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    setRemainingMs(0)
    setPhase('idle')
  }

  const handleFinish = async () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    stopBell()
    setPhase('finished')
    if (completedSets === 0) {
      toast.success('未完成任何组，未保存')
      return
    }
    try {
      await saveWorkout({
        exerciseKey: meta.exerciseKey,
        exerciseType: meta.exerciseType,
        startedAt: startedAtRef.current,
        endedAt: new Date(),
        durationSeconds: totalSeconds,
        summary: { totalSets: completedSets, setDuration },
      })
      toast.success('运动记录已保存')
    } catch {
      toast.error('保存失败')
    }
  }

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: '#12141D' }}>
      <PageHeader meta={meta} onBack={onBack} onSettings={onSettings} onTips={onTips} />
      <main className="flex-1 flex flex-col items-center px-6 pt-10 overflow-y-auto pb-6">
        <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">
          {phase === 'idle' && (completedSets === 0 ? '准备开始' : '准备下一组')}
          {phase === 'set' && '进行中'}
          {phase === 'set_ringing' && '本组结束 — 响铃中'}
          {phase === 'rest' && '组间休息'}
          {phase === 'rest_ringing' && '休息结束 — 响铃中'}
          {phase === 'finished' && '已结束'}
        </span>
        {phase === 'set_ringing' || phase === 'rest_ringing' ? (
          <Icon icon="lucide:bell-ring" className="mt-12 mb-6 w-24 h-24 text-amber-400 animate-bounce" />
        ) : (
          <div className={`mt-4 text-7xl font-mono font-extrabold tracking-tighter tabular-nums ${phase === 'rest' ? 'text-amber-400' : 'text-white'}`}>
            {formatMmSs(phase === 'idle' || phase === 'finished' ? setDuration * 1000 : remainingMs)}
          </div>
        )}
        <div className="mt-6 text-sm text-gray-400">已完成 <span className="text-white font-bold">{completedSets}</span> 组</div>

        {phase === 'idle' && (
          <div className="mt-8 w-full flex flex-col gap-3">
            <label className="text-xs text-gray-500 px-1">单组时长（秒）</label>
            <NumberField
              value={setDuration}
              onChange={setSetDuration}
              min={5}
              fallbackOnBlur={meta.defaultDuration ?? 60}
              className="w-full px-4 py-3 rounded-xl text-base font-bold text-white text-center outline-none"
              style={{ backgroundColor: '#1C1F2E', border: '1.5px solid #2A2D3A' }}
            />
          </div>
        )}
      </main>
      <div className="flex-none px-6 pb-10 flex gap-4">
        {(phase === 'idle' && completedSets === 0) && (
          <button onClick={startSet} className="w-full py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all" style={{ backgroundColor: '#10B981', borderColor: '#059669' }}>开始第 1 组</button>
        )}
        {(phase === 'idle' && completedSets > 0) && (
          <>
            <button onClick={handleFinish} className="flex-1 py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all" style={{ backgroundColor: '#1C1F2E', borderColor: '#151722' }}>结束</button>
            <button onClick={startSet} className="flex-1 py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all" style={{ backgroundColor: '#10B981', borderColor: '#059669' }}>开始第 {completedSets + 1} 组</button>
          </>
        )}
        {phase === 'set' && (
          <button disabled className="w-full py-4 rounded-2xl text-white text-lg font-bold opacity-60" style={{ backgroundColor: '#1C1F2E' }}>进行中…</button>
        )}
        {(phase === 'set_ringing' || phase === 'rest_ringing') && (
          <button onClick={stopBell} className="w-full py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all animate-pulse" style={{ backgroundColor: '#F59E0B', borderColor: '#D97706' }}>点击停止响铃</button>
        )}
        {phase === 'rest' && (
          <button onClick={skipRest} className="w-full py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all" style={{ backgroundColor: '#F59E0B', borderColor: '#D97706' }}>跳过休息</button>
        )}
        {phase === 'finished' && (
          <button onClick={onBack} className="w-full py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all" style={{ backgroundColor: '#1C1F2E', borderColor: '#151722' }}>返回首页</button>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Template 4: manual_reps — PushUp-style quick-tap buttons + custom input
// ============================================================================

type RepSet = { reps: number; weight?: number }
type TimedRepSet = RepSet & { doneAt: number }

/**
 * Split a manual_reps session by weight: same weight → one merged record, each
 * distinct weight → its own record (ordered by first use), so 40/40/30 kg saves
 * as "40kg × 2 组" + "30kg × 1 组". Mirrors Android `splitByWeight`. A part starts
 * where its first set started (session start, or when the previous set was logged)
 * and ends when its last set was logged; the part with the final set ends at finish.
 */
function splitByWeight(sets: TimedRepSet[], startMs: number, finishMs: number) {
  const groups = new Map<number | undefined, number[]>()
  sets.forEach((s, i) => {
    const g = groups.get(s.weight)
    if (g) g.push(i)
    else groups.set(s.weight, [i])
  })
  return [...groups.entries()].map(([weight, idx]) => {
    const first = idx[0]
    const last = idx[idx.length - 1]
    return {
      weight,
      sets: idx.map((i): RepSet => (sets[i].weight === undefined ? { reps: sets[i].reps } : { reps: sets[i].reps, weight: sets[i].weight })),
      startMs: first === 0 ? startMs : sets[first - 1].doneAt,
      endMs: last === sets.length - 1 ? finishMs : sets[last].doneAt,
    }
  })
}

function CustomManualReps({ meta, onBack, onSettings, onTips }: { meta: LocalCustomExercise; onBack: () => void; onSettings?: () => void; onTips?: () => void }) {
  type Phase = 'ready' | 'recording' | 'rest' | 'rest_ringing' | 'waiting_next' | 'finished'
  const [phase, setPhase] = useState<Phase>('ready')
  const [completedSets, setCompletedSets] = useState(0)
  const [totalReps, setTotalReps] = useState(0)
  const [remainingMs, setRemainingMs] = useState(0)
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [customReps, setCustomReps] = useState('')
  const [weight, setWeight] = useState<number>(meta.weight ?? 0)  // editable per session, default from meta
  const startedAtRef = useRef<Date>(new Date())
  const phaseEndRef = useRef<number>(0)
  const rafRef = useRef<number>(0)
  const cancelBellRef = useRef<(() => void) | null>(null)
  // Per-set log (reps + weight + when it was logged). On finish it is split by
  // weight so each distinct weight is saved as its own record.
  const setsLogRef = useRef<TimedRepSet[]>([])

  const restDur = meta.restDuration ?? 60
  const hasWeight = meta.weight !== null && meta.weight !== undefined
  const quickReps: number[] = [meta.quickReps1, meta.quickReps2, meta.quickReps3]
    .filter((n): n is number => typeof n === 'number' && n > 0)

  const tickRest = useCallback(() => {
    const remaining = Math.max(0, phaseEndRef.current - performance.now())
    setRemainingMs(remaining)
    if (remaining <= 0) {
      setPhase('rest_ringing')
      cancelBellRef.current = playBell(DEFAULT_BELL_COUNT, () => {
        cancelBellRef.current = null
        setPhase('waiting_next')
      })
      return
    }
    rafRef.current = requestAnimationFrame(tickRest)
  }, [])

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    if (cancelBellRef.current) { cancelBellRef.current(); cancelBellRef.current = null }
  }, [])

  const stopBell = () => {
    if (cancelBellRef.current) {
      cancelBellRef.current()
      cancelBellRef.current = null
    }
  }

  const handleStart = () => {
    startedAtRef.current = new Date()
    setPhase('recording')
  }

  const recordReps = (reps: number) => {
    setCompletedSets((s) => s + 1)
    setTotalReps((t) => t + reps)
    setsLogRef.current.push(hasWeight ? { reps, weight, doneAt: Date.now() } : { reps, doneAt: Date.now() })
    setShowCustomInput(false)
    setCustomReps('')
    phaseEndRef.current = performance.now() + restDur * 1000
    setRemainingMs(restDur * 1000)
    setPhase('rest')
    rafRef.current = requestAnimationFrame(tickRest)
  }

  const handleCustomSubmit = () => {
    const reps = Math.max(0, parseInt(customReps, 10) || 0)
    if (reps > 0) recordReps(reps)
  }

  const skipRest = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    stopBell()
    setRemainingMs(0)
    setPhase('waiting_next')
  }

  const handleFinish = async () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    stopBell()
    const wasRecording = phase !== 'ready'
    setPhase('finished')
    if (completedSets === 0 || !wasRecording) {
      toast.success('未完成任何组，未保存')
      return
    }
    try {
      const parts = splitByWeight(setsLogRef.current.slice(0, completedSets), startedAtRef.current.getTime(), Date.now())
      for (const p of parts) {
        const summary: Record<string, unknown> = {
          totalSets: p.sets.length,
          totalReps: p.sets.reduce((sum, s) => sum + s.reps, 0),
        }
        if (hasWeight) summary.weight = p.weight ?? 0
        summary.sets = p.sets
        await saveWorkout({
          exerciseKey: meta.exerciseKey,
          exerciseType: meta.exerciseType,
          startedAt: new Date(p.startMs),
          endedAt: new Date(p.endMs),
          durationSeconds: Math.round((p.endMs - p.startMs) / 1000),
          summary,
        })
      }
      toast.success(parts.length > 1 ? `已按重量保存为 ${parts.length} 条记录` : '运动记录已保存')
    } catch {
      toast.error('保存失败')
    }
  }

  const canRecord = phase === 'recording' || phase === 'waiting_next'

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: '#12141D' }}>
      <PageHeader meta={meta} onBack={onBack} onSettings={onSettings} onTips={onTips} />
      <main className="flex-1 flex flex-col items-center px-6 pt-8 overflow-y-auto pb-6">
        {/* Stats bar */}
        <div
          className="w-full flex justify-around items-center px-5 py-4 rounded-2xl"
          style={{ backgroundColor: '#1C1F2E', borderBottom: '4px solid #151722' }}
        >
          <div className="flex flex-col items-center">
            <span className="text-xs text-gray-500 font-medium">已完成</span>
            <span className="text-lg font-bold text-violet-400">{completedSets} 组</span>
          </div>
          <div className="w-px h-8 bg-[#2A2D3A]" />
          <div className="flex flex-col items-center">
            <span className="text-xs text-gray-500 font-medium">总次数</span>
            <span className="text-lg font-bold text-white">{totalReps}</span>
          </div>
        </div>

        {/* Phase indicator + big display */}
        <div className="mt-8 flex flex-col items-center">
          <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">
            {phase === 'ready' && '准备开始'}
            {phase === 'recording' && '进行中'}
            {phase === 'rest' && '组间休息'}
            {phase === 'rest_ringing' && '休息结束 — 响铃中'}
            {phase === 'waiting_next' && '准备下一组'}
            {phase === 'finished' && '已结束'}
          </span>
          {phase === 'rest_ringing' ? (
            <Icon icon="lucide:bell-ring" className="mt-12 mb-6 w-24 h-24 text-amber-400 animate-bounce" />
          ) : phase === 'rest' ? (
            <div className="mt-3 text-7xl font-mono font-extrabold tracking-tighter text-amber-400 tabular-nums">
              {formatMmSs(remainingMs)}
            </div>
          ) : (
            <div className="mt-3 text-6xl font-mono font-extrabold tracking-tighter text-white tabular-nums">
              {totalReps}
            </div>
          )}
        </div>

        {hasWeight && phase !== 'finished' && (
          <div className="mt-8 w-full flex items-center justify-center gap-3">
            <label className="text-xs text-gray-500 whitespace-nowrap">本次重量</label>
            <NumberField
              value={weight}
              onChange={setWeight}
              min={0}
              fallbackOnBlur={meta.weight ?? 0}
              className="w-28 px-3 py-2 rounded-lg text-base text-white text-center outline-none disabled:opacity-50"
              style={{ backgroundColor: '#1C1F2E', border: '1.5px solid #2A2D3A' }}
            />
            <span className="text-sm text-gray-400 whitespace-nowrap">kg</span>
          </div>
        )}
        {hasWeight && phase !== 'finished' && completedSets > 0 && (
          // Preview of how the session will be saved: one entry per distinct weight.
          <div className="mt-3 text-[13px] text-gray-500 text-center">
            {splitByWeight(setsLogRef.current.slice(0, completedSets), 0, 0)
              .map((p) => `${p.weight ?? 0}kg × ${p.sets.length}组 ${p.sets.reduce((sum, s) => sum + s.reps, 0)}次`)
              .join('　')}
          </div>
        )}
      </main>

      {/* Bottom Actions */}
      <div className="flex-none px-6 pb-10 flex flex-col gap-3">
        {phase === 'ready' && (
          <button
            onClick={handleStart}
            className="w-full py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all"
            style={{ backgroundColor: '#10B981', borderColor: '#059669' }}
          >开始运动</button>
        )}

        {canRecord && (
          <>
            {showCustomInput ? (
              <div className="flex gap-3">
                <input
                  type="number"
                  value={customReps}
                  onChange={(e) => setCustomReps(e.target.value)}
                  placeholder="输入次数"
                  autoFocus
                  className="flex-1 px-4 py-3 rounded-xl text-white text-sm font-medium outline-none"
                  style={{ backgroundColor: '#1C1F2E', border: '1.5px solid #2A2D3A' }}
                />
                <button
                  onClick={handleCustomSubmit}
                  className="px-5 py-3 rounded-xl text-white font-bold text-sm border-b-[4px] active:border-b-0 active:translate-y-[4px] transition-all"
                  style={{ backgroundColor: '#10B981', borderColor: '#059669' }}
                >确认</button>
                <button
                  onClick={() => { setShowCustomInput(false); setCustomReps('') }}
                  className="px-3 py-3 rounded-xl text-gray-400 text-sm"
                  style={{ backgroundColor: '#1C1F2E' }}
                >取消</button>
              </div>
            ) : (
              <div
                className="grid gap-2"
                style={{ gridTemplateColumns: `repeat(${quickReps.length + 1}, minmax(0, 1fr))` }}
              >
                {quickReps.map((n) => (
                  <button
                    key={n}
                    onClick={() => recordReps(n)}
                    className="py-3.5 rounded-2xl text-white font-bold text-sm border-b-[4px] active:border-b-0 active:translate-y-[4px] transition-all"
                    style={{ backgroundColor: '#8B5CF6', borderColor: '#7C3AED' }}
                  >{n}次</button>
                ))}
                <button
                  onClick={() => setShowCustomInput(true)}
                  className="py-3.5 rounded-2xl text-gray-300 font-bold text-sm border-b-[4px] active:border-b-0 active:translate-y-[4px] transition-all"
                  style={{ backgroundColor: '#1C1F2E', borderColor: '#151722' }}
                >自定义</button>
              </div>
            )}
          </>
        )}

        {phase === 'rest' && (
          <button
            onClick={skipRest}
            className="w-full py-3 rounded-2xl text-gray-400 font-bold text-sm"
            style={{ backgroundColor: '#1C1F2E' }}
          >跳过休息</button>
        )}

        {phase === 'rest_ringing' && (
          <button
            onClick={stopBell}
            className="w-full py-4 rounded-2xl text-white text-lg font-bold border-b-[4px] active:border-b-0 active:translate-y-[4px] transition-all animate-pulse"
            style={{ backgroundColor: '#F59E0B', borderColor: '#D97706' }}
          >点击停止响铃</button>
        )}

        {phase !== 'ready' && phase !== 'finished' && (
          <button
            onClick={handleFinish}
            className="w-full py-3.5 rounded-2xl text-red-400 text-base font-bold border-2 border-red-400/30"
          >结束运动</button>
        )}

        {phase === 'finished' && (
          <button
            onClick={onBack}
            className="w-full py-4 rounded-2xl text-white text-lg font-bold border-b-[6px] active:border-b-0 active:translate-y-[6px] transition-all"
            style={{ backgroundColor: '#1C1F2E', borderColor: '#151722' }}
          >返回首页</button>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Dispatcher
// ============================================================================

function CustomExercise() {
  const { exerciseKey } = useParams<{ exerciseKey: string }>()
  const navigate = useNavigate()
  const [meta, setMeta] = useState<LocalCustomExercise | null | 'loading'>('loading')
  const [showSettings, setShowSettings] = useState(false)
  const [showTips, setShowTips] = useState(false)

  const reloadMeta = useCallback(async () => {
    if (!exerciseKey) {
      setMeta(null)
      return
    }
    const row = await db.customExercises.get(exerciseKey)
    setMeta(row ?? null)
  }, [exerciseKey])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      if (!exerciseKey) {
        setMeta(null)
        return
      }
      const row = await db.customExercises.get(exerciseKey)
      if (cancelled) return
      setMeta(row ?? null)
    })()
    return () => { cancelled = true }
  }, [exerciseKey])

  if (meta === 'loading') {
    return (
      <div className="flex flex-col h-screen items-center justify-center" style={{ backgroundColor: '#12141D' }}>
        <Icon icon="lucide:loader-2" className="w-6 h-6 text-gray-500 animate-spin" />
      </div>
    )
  }

  if (!meta) {
    return (
      <div className="flex flex-col h-screen items-center justify-center gap-4" style={{ backgroundColor: '#12141D' }}>
        <p className="text-gray-400">未找到该自定义运动</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-3 rounded-2xl font-bold text-emerald-400"
          style={{ backgroundColor: '#1C1F2E' }}
        >返回</button>
      </div>
    )
  }

  const onBack = () => navigate(-1)
  // timer/laps_timer have nothing tunable today; other templates expose the gear
  const hasSettings = meta.template !== 'timer' && meta.template !== 'laps_timer'
  const onSettings = hasSettings ? () => setShowSettings(true) : undefined

  // Tips come from the static preset registry — only presets have tips today
  const tips = PRESET_BY_KEY[meta.exerciseKey]?.tips
  const onTips = tips && tips.length > 0 ? () => setShowTips(true) : undefined

  let page: ReactNode = null
  const subProps = { meta, onBack, onSettings, onTips }
  if (meta.template === 'timer') page = <CustomTimer {...subProps} />
  else if (meta.template === 'laps_timer') page = <CustomLapsTimer {...subProps} />
  else if (meta.template === 'countdown') page = <CustomCountdown {...subProps} />
  else if (meta.template === 'manual_reps') page = <CustomManualReps {...subProps} />

  return (
    <>
      {page}
      {showSettings && (
        <ExerciseSettingsSheet
          meta={meta}
          onClose={() => setShowSettings(false)}
          onSaved={async () => {
            await reloadMeta()
            setShowSettings(false)
          }}
        />
      )}
      {showTips && tips && (
        <TipsSheet
          name={meta.name}
          tips={tips}
          onClose={() => setShowTips(false)}
        />
      )}
    </>
  )
}

export default CustomExercise
